import React, { useState, useEffect } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { fetchHorizonsForSettings } from '../utils/accessibleHorizons';
import '../styles/HorizonAssignmentModal.css';

const HorizonAssignmentModal = ({ open, ingestionGroup, suffix, onClose, onSaved }) => {
  const { user } = useAuth();
  const [availableHorizons, setAvailableHorizons] = useState([]);
  const [selectedHorizons, setSelectedHorizons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !ingestionGroup) return;
    
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = await getAuthHeaders();

        const { horizons, error: hzErr } = await fetchHorizonsForSettings(getAuthHeaders, user);
        if (hzErr) {
          setError(hzErr);
        } else {
          setAvailableHorizons(horizons || []);
        }
        
        // Load existing assignments
        const assocResp = await fetch(
          `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(ingestionGroup.group_slug)}/horizons`,
          { headers }
        );
        const assocData = await assocResp.json();
        
        console.log('Existing assignments:', assocData);

        if (assocData.success && Array.isArray(assocData.data)) {
          if (hzErr) {
            setSelectedHorizons([]);
          } else {
            const allowed = new Set((horizons || []).map((h) => h.suffix).filter(Boolean));
            setSelectedHorizons(assocData.data.filter((s) => s && allowed.has(s)));
          }
        } else {
          setSelectedHorizons([]);
        }
      } catch (err) {
        console.error('Error loading horizon data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [open, ingestionGroup, suffix, user]);

  const toggleHorizon = (horizonSuffix) => {
    setSelectedHorizons(prev => {
      if (prev.includes(horizonSuffix)) {
        return prev.filter(s => s !== horizonSuffix);
      } else {
        return [...prev, horizonSuffix];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      
      console.log('Saving horizon assignments:', {
        suffix,
        groupSlug: ingestionGroup.group_slug,
        selectedHorizons,
        availableHorizons: availableHorizons.map(h => h.suffix)
      });
      
      const response = await fetch(
        `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(ingestionGroup.group_slug)}/horizons`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ horizon_suffixes: selectedHorizons })
        }
      );
      
      const data = await response.json();
      console.log('Save response:', data);
      
      if (!response.ok || !data.success) {
        let errorMsg = data.message || data.error || `Server returned ${response.status}`;
        if (data.invalid_suffixes && data.invalid_suffixes.length > 0) {
          errorMsg += `: ${data.invalid_suffixes.join(', ')}`;
        }
        throw new Error(errorMsg);
      }
      
      onSaved && onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving horizon assignments:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="horizon-assignment-modal">
        <div className="modal-header">
          <h3>
            <i className="fas fa-layer-group"></i>
            Assign Horizons
          </h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="modal-body">
          <div className="ingestion-info">
            <strong>{ingestionGroup?.name}</strong>
            <span className="slug">{ingestionGroup?.group_slug}</span>
          </div>
          
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              Loading horizons...
            </div>
          ) : (
            <>
              <div className="horizon-list">
                {availableHorizons.map((horizon) => {
                  const isSelected = selectedHorizons.includes(horizon.suffix);
                  return (
                    <div
                      key={horizon.suffix}
                      className={`horizon-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleHorizon(horizon.suffix)}
                    >
                      <div className="checkbox">
                        {isSelected && <i className="fas fa-check"></i>}
                      </div>
                      <div className="horizon-details">
                        <div className="horizon-suffix">{horizon.suffix}</div>
                        {horizon.description && (
                          <div className="horizon-description">{horizon.description}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="assignment-summary">
                {selectedHorizons.length === 0 ? (
                  <>
                    <i className="fas fa-exclamation-triangle warning-icon"></i>
                    <span>
                      <strong>Not assigned to any horizons.</strong> This source will not appear on any horizon data pages.
                    </span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle success-icon"></i>
                    <span>
                      <strong>Assigned to {selectedHorizons.length} horizon{selectedHorizons.length !== 1 ? 's' : ''}:</strong>{' '}
                      {selectedHorizons.join(', ')}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Save Assignments
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HorizonAssignmentModal;
